import { defineMessages } from 'react-intl';

import { createAction } from '@reduxjs/toolkit';

import { AxiosError } from 'axios';
import type { AxiosResponse } from 'axios';

import type { Alert } from 'mastodon/models/alert';
import { qaLog, qaErrorDetail } from 'mastodon/utils/qa_debug';

interface ApiErrorResponse {
  error?: string;
}

const messages = defineMessages({
  unexpectedTitle: { id: 'alert.unexpected.title', defaultMessage: 'Oops!' },
  unexpectedMessage: {
    id: 'alert.unexpected.message',
    defaultMessage: 'An unexpected error occurred.',
  },
  rateLimitedTitle: {
    id: 'alert.rate_limited.title',
    defaultMessage: 'Rate limited',
  },
  rateLimitedMessage: {
    id: 'alert.rate_limited.message',
    defaultMessage: 'Please retry after {retry_time, time, medium}.',
  },
});

// One toast per 5xx status per window. During an origin/Cloudflare outage
// every background poll fails at once and again every few seconds — without
// a lid the corner fills with an endless stream of "500"/"525" toasts.
// Repeats are still logged to the console for debugging.
const SERVER_ERROR_TOAST_WINDOW_MS = 30_000;
const recentServerErrorToasts = new Map<number, number>();

export const dismissAlert = createAction<{ key: number }>('alerts/dismiss');

export const clearAlerts = createAction('alerts/clear');

export const showAlert = createAction<Omit<Alert, 'key'>>('alerts/show');

const ignoreAlert = createAction('alerts/ignore');

export const showAlertForError = (error: unknown, skipNotFound = false) => {
  // QA observability: every error toast the QA account sees reaches the
  // server log with the underlying request — no-op for everyone else.
  qaLog('toast_error', 'error toast shown', qaErrorDetail(error));
  if (error instanceof AxiosError && error.response) {
    const { status, statusText, headers } = error.response;
    const { data } = error.response as AxiosResponse<ApiErrorResponse>;

    // Skip these errors as they are reflected in the UI
    if (skipNotFound && (status === 404 || status === 410)) {
      return ignoreAlert();
    }

    // Rate limit errors
    if (status === 429 && headers['x-ratelimit-reset']) {
      return showAlert({
        title: messages.rateLimitedTitle,
        message: messages.rateLimitedMessage,
        values: {
          retry_time: new Date(headers['x-ratelimit-reset'] as string),
        },
      });
    }

    if (status >= 500) {
      const now = Date.now();
      const last = recentServerErrorToasts.get(status);
      if (last !== undefined && now - last < SERVER_ERROR_TOAST_WINDOW_MS) {
        console.error(
          `[API] ${status} (duplicate toast suppressed)`,
          error.config?.url,
        );
        return ignoreAlert();
      }
      recentServerErrorToasts.set(status, now);
    }

    return showAlert({
      title: `${status}`,
      message: data.error ?? statusText,
    });
  }

  // An aborted request, e.g. due to reloading the browser window, is not really an error
  if (error instanceof AxiosError && error.code === AxiosError.ECONNABORTED) {
    return ignoreAlert();
  }

  console.error(error);

  return showAlert({
    title: messages.unexpectedTitle,
    message: messages.unexpectedMessage,
  });
};
